import type { SegmentationPreview } from "../predictions/segmentationPreviews";
import type { StoneFinding } from "./types";

const RENAL_REPORT_CONDITION = "kidneys/renal_stone";

/**
 * Adapt a renal prediction into the legacy renal-calculus report contract.
 *
 * Callers must gate on the canonical condition before using this bridge. The
 * generic segmentation pipeline deliberately does not know about StoneFinding.
 */
export function renalPredictionToReportStone(preview: SegmentationPreview): StoneFinding {
  if (preview.condition !== RENAL_REPORT_CONDITION) {
    throw new Error(`Cannot adapt non-renal condition ${preview.condition} to StoneFinding`);
  }
  return {
    id: preview.id,
    seriesInstanceUid: preview.seriesInstanceUid,
    seriesNumber: preview.seriesNumber,
    sliceIndex: preview.sliceIndex,
    sliceNumber: preview.sliceNumber,
    frameOfReferenceUid: preview.frameOfReferenceUid,
    measurements: {
      longAxisMm: preview.measurements.longAxisMm ?? 0,
      shortAxisMm: preview.measurements.shortAxisMm ?? 0,
      volumeMm3: preview.measurements.volumeMm3 ?? 0,
    },
    hu: { max: 0, mean: 0 },
    cropBoundsPx: preview.cropBoundsPx,
    longAxisPx: preview.longAxisPx,
    shortAxisPx: preview.shortAxisPx,
    confidence: preview.caseProbability,
    laterality: null,
    location: null,
  };
}

/**
 * Select predictions that the legacy renal-calculus report can represent.
 *
 * This is the sole condition-aware bridge from generic model segmentations to
 * StoneFinding. New segmentation conditions remain visible and annotatable,
 * but cannot silently acquire renal-report semantics.
 */
export function renalPredictionsToReportStones(
  previews: readonly SegmentationPreview[],
): StoneFinding[] {
  return previews
    .filter(
      (preview) =>
        preview.condition === RENAL_REPORT_CONDITION &&
        preview.measurements.longAxisMm != null &&
        preview.measurements.shortAxisMm != null,
    )
    .map(renalPredictionToReportStone);
}
